import { AnalyticsEventName } from '@/lib/consts/analytics';
import { SESSION_STORAGE_KEYS } from '@/lib/consts/sessionStorage';
import { collectAnalyticsEventProperties } from '@/lib/utils/analytics';
import { EConsentStatus } from '@web-store-platforms/components/cookie-consent/types';
import { LoggerEventNames } from '../consts/logger';
import { cookieConsentStorageAdapter } from './cookieConsentStorage';
import { getEnvVariable } from '@/lib/utils/env';

type MixpanelSdk = typeof import('@web-store-platforms/analytics/src/mixpanel');

type QueuedAnalyticsOperation =
    | {
        type: 'track';
        eventName: string;
        properties: Record<string, unknown>;
        includeDeviceProperties?: boolean;
    }
    | {
        type: 'identify';
        distinctId: string;
    }
    | {
        type: 'reset';
    };

/** Soft cap — drop-oldest with Coralogix breadcrumb when exceeded. */
const MAX_QUEUED_EVENTS = 250;
const MAX_INIT_ATTEMPTS = 2;

declare global {
    interface Window {
        gtag?: (
            command: 'config' | 'set' | 'event' | 'consent' | 'get',
            targetId: string,
            fieldNameOrParams?: string | Record<string, unknown>,
            callback?: (value: string) => void
        ) => void;
    }
}

class AnalyticsService {
    private isInitialized = false;
    private isOptedOut = false;
    private sdk: MixpanelSdk | undefined;
    private deviceProperties: Record<string, unknown> = {};
    private initializationPromise: Promise<void> | undefined;
    private readonly operationQueue: QueuedAnalyticsOperation[] = [];
    private failedInitAttempts = 0;
    private persistenceListenersAttached = false;
    private readonly readyResolvers: Array<() => void> = [];

    constructor() {
        if (typeof window === 'undefined') return;
        this.hydrateQueue();
        this.attachPersistenceListeners();
    }

    private updateGtagConsent(status: EConsentStatus): void {
        if (window.gtag) {
            window.gtag('consent', 'update', {
                analytics_storage: status,
                ad_storage: status,
            });
        }
    }

    initialize(): void {
        if (
            typeof window === 'undefined'
            || this.isInitialized
            || this.initializationPromise
            || this.isOptedOut
            || this.failedInitAttempts >= MAX_INIT_ATTEMPTS
        ) {
            return;
        }

        const consentStatus = cookieConsentStorageAdapter.getConsentStatus();
        if (consentStatus === EConsentStatus.DENIED) {
            this.isOptedOut = true;
            this.clearOperationQueue();
            this.notifyReady();
            return;
        }

        const token = getEnvVariable('NEXT_PUBLIC_MIXPANEL_TOKEN');
        if (!token) {
            console.warn('Mixpanel token is not configured');
            return;
        }

        this.hydrateQueue();

        this.initializationPromise = Promise.all([
            import('@web-store-platforms/analytics/src/mixpanel'),
            import('@/lib/utils/deviceAnalytics'),
        ])
            .then(([sdk, { collectDeviceAnalyticsEventProperties }]) => {
                if (this.isOptedOut) return;

                try {
                    this.deviceProperties = { ...collectDeviceAnalyticsEventProperties() };
                } catch (error) {
                    console.error('Failed to collect device analytics properties:', error);
                }
                sdk.initMixpanel({
                    token,
                    debug: getEnvVariable('NODE_ENV') === 'development',
                    batchRequests: false,
                });
                this.sdk = sdk;
                this.isInitialized = true;
                this.failedInitAttempts = 0;
                this.flushOperationQueue();
                this.notifyReady();
            })
            .catch((error) => {
                this.failedInitAttempts += 1;
                this.reportError('Failed to initialize Mixpanel', error);
            })
            .finally(() => {
                this.initializationPromise = undefined;
                if (this.isInitialized || this.isOptedOut) return;
                if (this.failedInitAttempts > 0 && this.failedInitAttempts < MAX_INIT_ATTEMPTS) {
                    void Promise.resolve().then(() => {
                        this.initialize();
                    });
                } else if (this.failedInitAttempts >= MAX_INIT_ATTEMPTS) {
                    this.notifyReady();
                }
            });
    }

    trackEvent(eventName: AnalyticsEventName, extraProperties: Record<string, unknown> = {}): void {
        if (typeof window === 'undefined' || this.isOptedOut) {
            return;
        }

        try {
            const properties = collectAnalyticsEventProperties();
            this.runOrQueue({
                type: 'track',
                eventName,
                properties: { ...properties, ...extraProperties },
                includeDeviceProperties: true,
            });
        } catch (error) {
            console.error('Failed to track event:', error);
        }
    }

    trackRawEvent(eventName: string, properties: Record<string, unknown> = {}): void {
        this.runOrQueue({ type: 'track', eventName, properties });
    }

    identifyUser(distinctId: string): void {
        this.runOrQueue({ type: 'identify', distinctId });
    }

    resetSession(): void {
        this.runOrQueue({ type: 'reset' });
    }

    optIn(): void {
        if (typeof window === 'undefined') return;

        try {
            cookieConsentStorageAdapter.grantConsent();
            this.isOptedOut = false;
            this.failedInitAttempts = 0;

            if (!this.isInitialized) {
                this.initialize();
            }

            if (this.isInitialized && this.sdk) {
                this.sdk.mixpanelOptIn();
            } else {
                void this.initializationPromise?.then(() => {
                    if (this.isInitialized && !this.isOptedOut) {
                        this.sdk?.mixpanelOptIn();
                    }
                });
            }

            this.updateGtagConsent(EConsentStatus.GRANTED);
        } catch (error) {
            console.error('Error during analytics opt-in:', error);
        }
    }

    optOut(): void {
        if (typeof window === 'undefined') return;

        try {
            cookieConsentStorageAdapter.denyConsent();
            this.isOptedOut = true;
            this.clearOperationQueue();
            this.notifyReady();

            if (this.isInitialized && this.sdk) {
                this.sdk.mixpanelOptOut();
            }

            this.updateGtagConsent(EConsentStatus.DENIED);
        } catch (error) {
            this.reportError('Failed to opt out of analytics', error);
        }
    }

    isReady(): boolean {
        return this.isInitialized && !this.isOptedOut && typeof window !== 'undefined';
    }

    hasOptedOut(): boolean {
        return this.isOptedOut;
    }

    /**
     * Resolves when Mixpanel has initialized, init has permanently failed,
     * or analytics has been opted out. Does not gate UI.
     */
    whenReady(): Promise<void> {
        if (typeof window === 'undefined') return Promise.resolve();
        if (this.isInitialized || this.isOptedOut || this.failedInitAttempts >= MAX_INIT_ATTEMPTS) {
            return Promise.resolve();
        }
        return new Promise((resolve) => {
            this.readyResolvers.push(resolve);
        });
    }

    private notifyReady(): void {
        this.readyResolvers.splice(0).forEach((resolve) => {
            resolve();
        });
    }

    private runOrQueue(operation: QueuedAnalyticsOperation): void {
        if (typeof window === 'undefined' || this.isOptedOut) return;

        if (this.isInitialized && this.sdk) {
            this.runOperation(operation);
            return;
        }

        if (this.operationQueue.length >= MAX_QUEUED_EVENTS) {
            const dropped = this.operationQueue.shift();
            this.reportQueueDrop(dropped);
        }
        this.operationQueue.push(operation);
        this.persistQueue();
    }

    private runOperation(operation: QueuedAnalyticsOperation): void {
        if (!this.sdk) return;

        if (operation.type === 'track') {
            this.sdk.trackMixpanelEvent(
                operation.eventName,
                operation.includeDeviceProperties
                    ? { ...this.deviceProperties, ...operation.properties }
                    : operation.properties
            );
        } else if (operation.type === 'identify') {
            this.sdk.identifyMixpanelUser(operation.distinctId);
        } else {
            this.sdk.resetMixpanelSession();
        }
    }

    private flushOperationQueue(): void {
        if (!this.sdk || !this.isInitialized || this.isOptedOut) return;

        this.operationQueue.splice(0).forEach((operation) => {
            this.runOperation(operation);
        });
        this.clearPersistedQueue();
    }

    private clearOperationQueue(): void {
        this.operationQueue.length = 0;
        this.clearPersistedQueue();
    }

    private persistQueue(): void {
        if (typeof window === 'undefined') return;
        try {
            if (this.operationQueue.length === 0) {
                this.clearPersistedQueue();
                return;
            }
            sessionStorage.setItem(
                SESSION_STORAGE_KEYS.ANALYTICS_OPERATION_QUEUE,
                JSON.stringify(this.operationQueue),
            );
        } catch {
            /* sessionStorage unavailable — in-memory queue only */
        }
    }

    private clearPersistedQueue(): void {
        if (typeof window === 'undefined') return;
        try {
            sessionStorage.removeItem(SESSION_STORAGE_KEYS.ANALYTICS_OPERATION_QUEUE);
        } catch {
            /* ignore */
        }
    }

    private hydrateQueue(): void {
        if (
            typeof window === 'undefined'
            || this.isOptedOut
            || this.isInitialized
            || this.operationQueue.length > 0
        ) {
            return;
        }
        try {
            const raw = sessionStorage.getItem(SESSION_STORAGE_KEYS.ANALYTICS_OPERATION_QUEUE);
            if (!raw) return;
            const parsed: unknown = JSON.parse(raw);
            if (!Array.isArray(parsed) || parsed.length === 0) return;

            for (const item of parsed) {
                if (!isQueuedAnalyticsOperation(item)) continue;
                if (this.operationQueue.length >= MAX_QUEUED_EVENTS) {
                    const dropped = this.operationQueue.shift();
                    this.reportQueueDrop(dropped);
                }
                this.operationQueue.push(item);
            }
        } catch {
            this.clearPersistedQueue();
        }
    }

    private attachPersistenceListeners(): void {
        if (this.persistenceListenersAttached || typeof window === 'undefined') return;
        this.persistenceListenersAttached = true;

        const persistIfNeeded = () => {
            if (!this.isInitialized && !this.isOptedOut && this.operationQueue.length > 0) {
                this.persistQueue();
            }
        };

        window.addEventListener('pagehide', persistIfNeeded);
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                persistIfNeeded();
            }
        });
    }

    private reportQueueDrop(dropped: QueuedAnalyticsOperation | undefined): void {
        if (!dropped) return;
        void import('./coralogixRum')
            .then(({ default: coralogixRumService }) => {
                coralogixRumService.sendInfoEvent(LoggerEventNames.ANALYTICS_ERROR, {
                    message: 'Analytics operation queue overflow; dropped oldest operation',
                    droppedType: dropped.type,
                    ...(dropped.type === 'track' ? { eventName: dropped.eventName } : {}),
                });
            })
            .catch(() => {
                /* ignore reporting failures */
            });
    }

    private reportError(message: string, error: unknown): void {
        void import('./coralogixRum')
            .then(({ default: coralogixRumService }) => {
                coralogixRumService.sendErrorEvent(LoggerEventNames.ANALYTICS_ERROR, {
                    message,
                    error,
                });
            })
            .catch((reportingError) => {
                console.error(message, error, reportingError);
            });
    }
}

function isQueuedAnalyticsOperation(value: unknown): value is QueuedAnalyticsOperation {
    if (!value || typeof value !== 'object') return false;
    const op = value as { type?: unknown };
    if (op.type === 'reset') return true;
    if (op.type === 'identify') {
        return typeof (value as { distinctId?: unknown }).distinctId === 'string';
    }
    if (op.type === 'track') {
        const track = value as { eventName?: unknown; properties?: unknown };
        return typeof track.eventName === 'string'
            && !!track.properties
            && typeof track.properties === 'object';
    }
    return false;
}

export default new AnalyticsService();
